import { API_BASE_URL } from "@/config";
import { PREPOPULATE_INPUT } from "@/utils/prepopulate_input.js";

export default {
  name: "PersonalDetails",
  mixins: [PREPOPULATE_INPUT],
  props: {
    getSavedSectionResponses: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // Form fields
      client_category: "",
      first_name: "",
      last_name: "",
      client_preferred_name: "",
      id_number: "",
      client_birth_country: "",
      mobile_contract_type: "",
      contact_method: "",
      client_correspondence_language: "",
      client_home_language: "",
      client_ethnic_group: "",
      client_education_level: "",
      client_foreign_affiliation_ind: null,
      relative_first_name: "",
      relative_last_name: "",
      relative_mobile_number: "",
      relative_relation: "",

      // For API integration
      questions: [], // Store fetched questions
      question_map: {}, // Maps api_form_field to question_id
      error_messages: {}, // Validation errors
      is_field_disabled: {},

      // Options for select fields
      client_category_options: [
        { value: "PRIVATE", label: "Private" },
        { value: "COMMERCIAL", label: "Commercial" },
      ],
      mobile_contract_type_options: [
        { value: "CONTRACT", label: "Contract" },
        { value: "PREPAID", label: "Prepaid" },
      ],
      contact_method_options: [
        { value: "EMAIL", label: "Email" },
        { value: "WORK PHONE", label: "Work Phone" },
        { value: "MOBILE", label: "Mobile Phone" },
      ],
      language_options: [
        { value: "ENGLISH", label: "English" },
        { value: "AFRIKAANS", label: "Afrikaans" },
        { value: "ISIXHOSA", label: "isiXhosa" },
        { value: "ISIZULU", label: "isiZulu" },
        { value: "SESOTHO", label: "Sesotho" },
        { value: "SETSWANA", label: "Setswana" },
        { value: "SISWATI", label: "siSwati" },
        { value: "TSHIVENDA", label: "Tshivenda" },
        { value: "XITSONGA", label: "Xitsonga" },
        { value: "SESOTHO SA LEBOWA", label: "Sesotho sa Lebowa" },
        { value: "ISINDEBELE", label: "isiNdebele" },
      ],
      ethnic_group_options: [
        { value: "ASIAN", label: "Asian" },
        { value: "BLACK", label: "Black" },
        { value: "COLOURED", label: "Coloured" },
        { value: "WHITE", label: "White" },
        { value: "INDIAN", label: "Indian" },
      ],
      education_level_options: [
        { value: "NO SCHOOLING", label: "No Schooling" },
        { value: "INCOMPLETE PRIMARY", label: "Incomplete Primary" },
        {
          value: "COMPLETED PRIMARY (GRADE 7)",
          label: "Completed Primary (Grade 7)",
        },
        {
          value: "GET CERTIFICATE (GRADE 9)",
          label: "GET Certificate (Grade 9)",
        },
        {
          value: "MATRIC CERTIFICATE (GRADE 12)",
          label: "Matric Certificate (Grade 12)",
        },
        { value: "DIPLOMA", label: "Diploma" },
        { value: "BACHELORS DEGREE", label: "Bachelors Degree" },
        { value: "POSTGRADUATE DIPLOMA", label: "Postgraduate Diploma" },
        { value: "HONOURS DEGREE", label: "Honours Degree" },
        {
          value: "PROFESSIONAL QUALIFICATION",
          label: "Professional Qualification",
        },
        { value: "MASTERS DEGREE", label: "Masters Degree" },
        { value: "DOCTORATE", label: "Doctorate" },
      ],
      relative_relation_options: [
        { value: "PARENT", label: "Parent" },
        { value: "SIBLING", label: "Sibling" },
        { value: "CHILD", label: "Child" },
        { value: "DISTANT", label: "Distant Relative" },
      ],
      country_options: [
        { value: "SOUTH AFRICA", label: "South Africa" },
      ],
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
  },
  methods: {
    handle_client_category_change() {
      // Reset form fields if category changes
      if (this.client_category === "COMMERCIAL") {
        // Reset personal fields if switching to commercial
        this.first_name = "";
        this.last_name = "";
        this.client_preferred_name = "";
        // Reset other fields...
      }

      // Clear any existing validation errors
      this.error_messages = {};
      this.get_customer_data();
    },

    get_questions() {
      const TOKEN = localStorage.getItem("token");
      const GET_QUESTIONS_URL = `${API_BASE_URL}/verify_api/financial_information_api/get_personal_questions/`;

      $.ajax({
        url: GET_QUESTIONS_URL,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Fetching questions...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: (response) => {
          Swal.close();
          this.questions = [...response.questions];

          // Map each question's api_form_field to its id
          this.questions.forEach((question) => {
            if (question.api_form_field) {
              this.question_map[question.api_form_field] = question.id;
            }
          });

          this.load_saved_responses();
        },
        error: () => {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch personal questions. Please try again later.",
            confirmButtonText: "OK",
          });
        },
      });
    },
    validate_personal_form() {
      this.error_messages = {}; // Reset errors

      // First validate client_category regardless of other fields
      if (!this.client_category) {
        this.error_messages.client_category =
          "Please select a transaction type.";
        return false;
      }

      // If commercial is selected, no need to validate personal fields
      if (this.client_category === "COMMERCIAL") {
        // Additional commercial validation would go here
        return true;
      }

      // Validate each field based on question requirements
      this.questions.forEach((question) => {
        const field_name = question.api_form_field;
        if (!field_name) return; // Skip if no field mapping

        // Check if field is required and empty
        if (question.required && !this[field_name]) {
          this.error_messages[field_name] = `${question.question} is required.`;
        }
      });

      // If no questions were loaded, fall back to hardcoded validation
      if (this.questions.length === 0) {
        if (!this.first_name)
          this.error_messages.first_name = "First name is required.";
        if (!this.last_name)
          this.error_messages.last_name = "Last name is required.";
        if (!this.id_number)
          this.error_messages.id_number = "ID number is required.";
        if (!this.client_birth_country)
          this.error_messages.client_birth_country =
            "Country of birth is required.";
        if (!this.mobile_contract_type)
          this.error_messages.mobile_contract_type =
            "Mobile contract type is required.";
        if (!this.contact_method)
          this.error_messages.contact_method = "Contact method is required.";
        if (!this.client_foreign_affiliation_ind)
          this.error_messages.client_foreign_affiliation_ind =
            "Foreign affiliation selection is required.";
        if (!this.client_correspondence_language)
          this.error_messages.client_correspondence_language =
            "Correspondence language is required.";
        if (!this.client_home_language)
          this.error_messages.client_home_language =
            "Home language is required.";
        if (!this.client_ethnic_group)
          this.error_messages.client_ethnic_group = "Ethnic group is required.";
        if (!this.client_education_level)
          this.error_messages.client_education_level =
            "Education level is required.";
        if (!this.relative_first_name)
          this.error_messages.relative_first_name =
            "Relative's first name is required.";
        if (!this.relative_last_name)
          this.error_messages.relative_last_name =
            "Relative's last name is required.";
        if (!this.relative_mobile_number)
          this.error_messages.relative_mobile_number =
            "Relative's mobile number is required.";
        if (!this.relative_relation)
          this.error_messages.relative_relation =
            "Relative's relationship is required.";
      }
      return Object.keys(this.error_messages).length === 0;
    },
    submit_personal_details(section = null, customer_id = null) {
      // Handle case where section is a DOM event (from template click)
      if (
        section &&
        (section instanceof Event || section.type === "click" || section.target)
      ) {
        section = null;
      }

      if (!section && !this.validate_personal_form()) {
        console.error("Validation errors:", this.error_messages);
        Swal.fire({
          icon: "warning",
          title: "Incomplete Form",
          text: "Please complete all required fields before continuing.",
          confirmButtonText: "OK",
        });
        return;
      }

      const TOKEN = localStorage.getItem("token");
      const SAVE_RESPONSES_URL = `${API_BASE_URL}/verify_api/financial_information_api/save_personal_details/`;

      // Prepare responses by mapping form values back to question_ids
      let responses = [];

      if (section && Array.isArray(section.questions)) {
        responses = section.questions.map((q) => ({
          question_id: q.question_id,
          answer: q.response?.toString() || "",
        }));
      } else {
        // Process each field in our data model
        Object.keys(this.$data).forEach((field_name) => {
          // Skip non-form fields or empty values
          if (
            !this.question_map[field_name] ||
            field_name === "questions" ||
            field_name === "question_map" ||
            field_name === "error_messages"
          ) {
            return;
          }

          if (
            this.client_category === "COMMERCIAL" &&
            field_name !== "client_category"
          ) {
            return;
          }

          let value = this[field_name];

          // Add to responses array
          responses.push({
            question_id: this.question_map[field_name],
            answer: value || "",
          });
        });
      }

      const FORM_DATA = {
        workflow_id: this.workflow_id,
        responses: responses,
      };

      if (customer_id) {
        FORM_DATA.customer_id = customer_id;
      }

      $.ajax({
        url: SAVE_RESPONSES_URL,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(FORM_DATA),
        beforeSend: () => {
          Swal.fire({
            text: "Saving personal details...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: () => {
          Swal.close();
          if (section && customer_id) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Personal details changed successfully!",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            this.$emit("validity-change", true);
          }
        },
        error: (xhr) => {
          Swal.close();
          let errorMessage =
            "Failed to save responses. Please try again later.";
          if (xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error;
          }
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonText: "OK",
          });
        },
      });
    },
    async load_saved_responses() {
      try {
        const responses = await this.getSavedSectionResponses(
          "Personal Details"
        );

        const idToField = {};
        Object.entries(this.question_map).forEach(([field, qid]) => {
          idToField[qid] = field;
        });

        if (Array.isArray(responses) && responses.length > 0) {
          responses.forEach((resp) => {
            const field = idToField[resp.question_id];
            if (field && Object.prototype.hasOwnProperty.call(this, field)) {
              this[field] = resp.answer;
            }
          });
        }
      } catch (error) {
        console.error("Error loading saved responses:", error);
      }
    },
  },
  mounted() {
    if (this.$store.getters.get_user_role?.toLowerCase() === "customer") {
      this.get_questions();
      this.get_customer_data();
    }
  },
};
